import { environment } from "../../../environments/environment";
import { API_ENDPOINTS } from "../../core/constants/api-endpoints";
import { UserService } from "../../core/services/user.service";
import { MealService } from "../../core/services/meal.service";
import { Router } from "@angular/router";
import { DateSelectionService } from "../../services/date-selection.service";
import { getProgramId, getSelectedLocation } from "./storage-utils";
import { FormDateUtils } from "./form-date-utils";

function convertValueToString(value: unknown): string {
  const valueType = typeof value;
  
  if (valueType === 'object' && value !== null) {
    // All objects (including arrays, Date, RegExp, etc.) should be stringified
    // Check for common object types that need special handling
    if (value instanceof Date) {
      return value.toISOString();
    }
    if (value instanceof RegExp) {
      return value.toString();
    }
    // Plain objects, arrays, and other objects - explicitly stringify to avoid Object's default stringification
    return JSON.stringify(value);
  }
  
  // Primitive values (string, number, boolean, etc.) - convert to string
  return String(value);
}

export function appendQuery(endpoint: string, payload: Record<string, unknown>): string {
    const params = new URLSearchParams();

    Object.entries(payload ?? {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        // Handle object stringification properly - explicitly convert to string
        const stringValue = convertValueToString(value);
        params.append(key, stringValue);
      }
    });

    const query = params.toString();
    return query ? `${endpoint}?${query}` : endpoint;
  }

  export function getImageUrl(fileName: string): string {
    const raw = (fileName ?? '').trim();
    if (!raw) {
      return '';
    }
    if (/^(https?:|data:|blob:)/i.test(raw) || raw.startsWith('/assets/')) {
      return raw;
    }
    return `${environment.host}${API_ENDPOINTS.FILE.GET(raw)}`;
  }

export function formatTime(time: string): string {
    // Convert to 12‑hour clock with zero-padded minutes/seconds and AM/PM suffix
    const date = new Date(time);
    if (isNaN(date.getTime())) {
      return time;
    }

    let hours = date.getHours();
    const period = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');

    return `${hours}:${minutes}:${seconds} ${period}`;
  }

export interface BaseDateOption {
  dayLabel: string;
  dateLabel: string;
  dateValue: string;
}

export function generateDateOptions(days: number = 4): BaseDateOption[] {
  const options: BaseDateOption[] = [];
  // Always use Houston calendar day so kiosks/testers in other TZs match Baker Ripley ops.
  const today = FormDateUtils.getHoustonTodayDate();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();
  const todayDate = today.getDate();

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  let offset = 0;
  while (options.length < days) {
    const date = new Date(todayYear, todayMonth, todayDate + offset);
    offset += 1;

    const weekday = date.getDay();
    if (weekday === 0 || weekday === 6) {
      continue;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateValue = `${year}-${month}-${day}`;

    const dayLabel = options.length === 0 ? 'Today' : dayNames[weekday];
    const dateLabel = `${date.getDate()} ${monthNames[date.getMonth()]}`;

    options.push({
      dayLabel,
      dateLabel,
      dateValue
    });
  }

  return options;
}

/**
 * Interface for date options with badge count
 */
export interface DateOptionWithBadge extends BaseDateOption {
  badgeCount?: number;
  submitted?: boolean;
  locationName?: string;
  /** Daily hot / special menu text from meal_menu (Figma date cards) */
  hotMenuText?: string;
  /** Daily cold menu text from meal_menu */
  coldMenuText?: string;
}

export interface MealMenuDaySource {
  day?: string | null;
  hotMeal?: string | null;
  coldMeal?: string | null;
  vegHotMeal?: string | null;
  vegColdMeal?: string | null;
  nonVegHotMeal?: string | null;
  nonVegColdMeal?: string | null;
  specialEvents?: string | null;
}

export type MealDietPreference = 'vegetarian' | 'non-vegetarian' | null;

/** True when Client Preferences meal type is Vegetarian (not Non-Vegetarian). */
export function isVegetarianMealDiet(preference: string | null | undefined): boolean {
  if (!preference) {
    return false;
  }
  const normalized = preference
    .trim()
    .toLowerCase()
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ');
  return normalized.includes('vegetarian') && !normalized.includes('non vegetarian');
}

export function resolveMealDietPreference(
  preference: string | null | undefined,
): MealDietPreference {
  if (!preference || !String(preference).trim()) {
    return null;
  }
  return isVegetarianMealDiet(preference) ? 'vegetarian' : 'non-vegetarian';
}

function pickMenuHotCold(
  menu: MealMenuDaySource | undefined,
  diet: MealDietPreference,
): { hot: string; cold: string } {
  if (!menu) {
    return { hot: '', cold: '' };
  }

  if (diet === 'vegetarian') {
    return {
      hot: (menu.vegHotMeal ?? menu.hotMeal ?? '').trim(),
      cold: (menu.vegColdMeal ?? menu.coldMeal ?? '').trim(),
    };
  }

  if (diet === 'non-vegetarian') {
    return {
      hot: (menu.nonVegHotMeal ?? menu.hotMeal ?? '').trim(),
      cold: (menu.nonVegColdMeal ?? menu.coldMeal ?? '').trim(),
    };
  }

  // Backend may already resolve hotMeal/coldMeal when userid was passed.
  return {
    hot: (menu.hotMeal ?? menu.nonVegHotMeal ?? menu.vegHotMeal ?? '').trim(),
    cold: (menu.coldMeal ?? menu.nonVegColdMeal ?? menu.vegColdMeal ?? '').trim(),
  };
}

/** Resolve weekday name for a YYYY-MM-DD value (used when card label is "Today"). */
export function weekdayNameFromDateValue(dateValue: string): string {
  if (!dateValue) {
    return '';
  }
  const date = new Date(`${dateValue}T12:00:00`);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()] ?? '';
}

/**
 * Attach meal_menu hot/cold text to date cards (Figma: show menu on each day card).
 * When dietPreference is vegetarian / non-vegetarian, uses the matching menu columns.
 */
export function applyMealMenusToDateOptions<T extends BaseDateOption>(
  options: T[],
  menus: MealMenuDaySource[] | null | undefined,
  dietPreference?: string | MealDietPreference | null,
): Array<T & { hotMenuText: string; coldMenuText: string }> {
  const byDay = new Map<string, MealMenuDaySource>();
  for (const menu of menus ?? []) {
    const key = (menu.day ?? '').trim().toLowerCase();
    if (key) {
      byDay.set(key, menu);
    }
  }

  const diet =
    dietPreference === 'vegetarian' || dietPreference === 'non-vegetarian'
      ? dietPreference
      : resolveMealDietPreference(dietPreference ?? null);

  return options.map((option) => {
    const weekday =
      option.dayLabel?.toLowerCase() === 'today'
        ? weekdayNameFromDateValue(option.dateValue)
        : option.dayLabel;
    const menu = byDay.get((weekday ?? '').trim().toLowerCase());
    const { hot, cold } = pickMenuHotCold(menu, diet);
    return {
      ...option,
      hotMenuText: hot,
      coldMenuText: cold,
    };
  });
}

/**
 * Common function to fetch user count and update date options with badge counts
 * @param userService - UserService instance
 * @param dateOptions - Array of date options to update
 * @param onSuccess - Callback function called on successful response with updated options and full response
 * @param onError - Callback function called on error
 */
export function getUserCount(
  userService: UserService,
  dateOptions: DateOptionWithBadge[],
  onSuccess?: (updatedOptions: DateOptionWithBadge[], response?: any) => void,
  onError?: (error: any) => void
): void {
  if (!dateOptions.length) {
    return;
  }

  const location = getSelectedLocation();
  const payload: any = {
    programId: getProgramId(),
    date: dateOptions.map(option => option.dateValue ?? ''),
    ...(location ? { locationId: location.id } : {})
  };

  userService.getUsersCount(payload).subscribe({
    next: (response) => {
      const countsMap = new Map<string, number>();
      response?.data?.forEach((entry: any) => {
        if (entry?.date) {
          countsMap.set(entry.date, entry?.counts?.pending ?? 0);
        }
      });

      const updatedOptions = dateOptions.map(option => ({
        ...option,
        badgeCount: option.dateValue ? (countsMap.get(option.dateValue) ?? 0) : 0
      }));

      if (onSuccess) {
        onSuccess(updatedOptions, response);
      }
    },
    error: (error) => {
      if (onError) {
        onError(error);
      }
    }
  });
}

/**
 * Common function to fetch meal data
 * @param mealService - MealService instance
 * @param userId - User ID from user details
 * @param dateOptions - Array of date options or single date string
 * @param onSuccess - Callback function called on successful response with meal data
 * @param onError - Callback function called on error
 */
export function getMealData(
  mealService: MealService,
  userId: string,
  dateOptions: BaseDateOption[] | string | string[],
  onSuccess?: (mealData: any) => void,
  onError?: (error: any) => void
): void {
  let dateValues: string[];
  
  if (typeof dateOptions === 'string') {
    // Single date string
    dateValues = [dateOptions];
  } else if (Array.isArray(dateOptions) && dateOptions.length > 0 && typeof dateOptions[0] === 'string') {
    // Array of date strings
    dateValues = dateOptions as string[];
  } else {
    // Array of BaseDateOption objects
    dateValues = (dateOptions as BaseDateOption[]).map(option => option.dateValue ?? '').filter(d => d);
  }
  
  const payload = {
    userId: userId,
    date: dateValues,
    programId: getProgramId(),
  };

  mealService.getMealInfo(payload).subscribe({
    next: (response: any) => {
      if (onSuccess) {
        onSuccess(response);
      }
    },
    error: (error: any) => {
      if (onError) {
        onError(error);
      }
    }
  });
}

/**
 * Interface for meal preference option with waitlist count
 */
export interface MealPreferenceOption {
  id: string;
  waitlistCount: number;
}

/**
 * Common function to fetch preference counts (Hot/Cold meal preferences)
 * @param userService - UserService instance
 * @param mealOptions - Array of meal preference options to update with waitlist counts
 * @param onSuccess - Callback function called on successful response with full response data
 * @param onError - Callback function called on error
 * @param date - Optional date string to filter preference counts by date
 */
export function getPreferenceCounts(
  userService: UserService,
  mealOptions: MealPreferenceOption[],
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void,
  date?: string | null
): void {
  const location = getSelectedLocation();
  const payload: any = {
    programId: getProgramId(),
    ...(location ? { locationId: location.id } : {})
  };

  if (date) {
    payload.date = [date];
  }

  userService.getUsersCount(payload).subscribe({
    next: (response) => {
      // Update waitlist counts for meal options
      if (response?.data?.length > 0) {
        const preferenceCounts = response.data[0]?.counts?.preferenceCounts?.preferences ?? {};
        
        mealOptions.forEach(option => {
          if (option.id === "Hot") {
            option.waitlistCount = preferenceCounts.Hot ?? 0;
          } else if (option.id === "Cold") {
            option.waitlistCount = preferenceCounts.Cold ?? 0;
          }
        });
      } else {
        // Reset counts if no data
        mealOptions.forEach(option => {
          option.waitlistCount = 0;
        });
      }

      if (onSuccess) {
        onSuccess(response);
      }
    },
    error: (error) => {
      if (onError) {
        onError(error);
      }
    }
  });
}

/**
 * Common function to handle back navigation
 * Clears editing date state and navigates to previous route or fallback route
 * @param router - Router instance for navigation
 * @param dateSelectionService - DateSelectionService instance to clear editing date state
 * @param fallbackRoute - Optional fallback route if previous route is not found (default: '/scheduled-confirmed')
 */
export function handleBackNavigation(
  router: Router,
  dateSelectionService: DateSelectionService,
  fallbackRoute: string = '/scheduled-confirmed'
): void {
  // Clear editing date state if it exists
  const editingDateIndex = dateSelectionService.getCurrentEditingDate();
  if (editingDateIndex !== null) {
    dateSelectionService.setCurrentEditingDate(null);
  }
  
  // Get the previous route from sessionStorage (stored when navigating to this page)
  const previousRoute = sessionStorage.getItem('previousRoute');
  
  if (previousRoute) {
    // Navigate to the stored previous route
    sessionStorage.removeItem('previousRoute'); // Clean up
    router.navigate([previousRoute]); 
    return;
  }
  
  // Fallback: navigate to the specified fallback route
  // This handles cases where previousRoute wasn't set or user navigated directly
  router.navigate([fallbackRoute]);
}
